import { differenceInDays } from 'date-fns';
import {
  applyStatus as APPLY_STATUS,
  WARRANTY_DOCUMENT_TYPE,
  WARRANTY_STATUS,
  warrantyCoverageType,
  warrantyType,
} from '@/consts/warranty';
import {
  Car,
  MWarrantyPart,
  NextWarranty,
  NextWarrantyAppendWarranty,
  WarrantiesAppendWarranty,
  Warranty,
} from '@/types/api/customerApi';
import { ApplyStatus } from '@/types/warranty';

import { WarrantyCoverageType } from '@/types/warranty';

export type WarrantyPartsType = WarrantyPart[];

const NEXT_WARRANTY_BRANCH_INCREMENT = 1;

export type WarrantyPart = {
  m_accessory_category_name: string;
  subItems: MWarrantyPart[];
};

export type WarrantyGroup = {
  [index: string]: MWarrantyPart[];
};

export const getWarrantyPartsData = (warrantyList: MWarrantyPart[]) => {
  const groups: WarrantyGroup = {};
  warrantyList.forEach((el) => {
    const group = el['parts_category_name'] ?? '';
    groups[group] = groups[group] || [];
    groups[group].push(el);
  });
  return Object.keys(groups).map((group) => {
    const data: WarrantyPart = {
      m_accessory_category_name: group,
      subItems: groups[group],
    };
    return data;
  });
};

export const hasWarrantyTiredToCar = (car: Car | undefined) => {
  return !!car?.warranties?.[0]?.warranty_no;
};

export const findDefectAppendWarranty = (warranty: Warranty | undefined) => {
  return warranty?.append_warranties?.find(
    (w: WarrantiesAppendWarranty) => w.warranty_type === warrantyType.DEFECT,
  );
};

/**
 * next_warranty.append_warranties を配列に正規化する。
 *
 * API(/v1/customer/info) は、次期保証レコードが未作成のとき現保証の特約から次期保証を組み立てるが、
 * その際 auto_continuous_flg=0 の特約を配列の途中で読み飛ばすためキーに欠番が生じ、
 * JSON オブジェクトとして返ることがある（dev 実測: `{"1":…,"2":…}`）。
 * 外側の next_warranty には array_values が入っているが内側には入っていない（＝BE 側の漏れ）。
 * 配列前提の走査（find / slice）が TypeError で落ちるため、走査側で吸収する。
 */
export const toNextAppendWarrantyList = (
  appendWarranties:
    | NextWarrantyAppendWarranty[]
    | Record<string, NextWarrantyAppendWarranty>
    | undefined
    | null,
): NextWarrantyAppendWarranty[] =>
  Array.isArray(appendWarranties)
    ? appendWarranties
    : Object.values(appendWarranties ?? {});

export const findNextDefectAppendWarranty = (
  warranty: NextWarranty | undefined,
) => {
  return toNextAppendWarrantyList(warranty?.append_warranties).find(
    (w: NextWarrantyAppendWarranty) => w.warranty_type === warrantyType.DEFECT,
  );
};

/**
 * 保証の「代表となる付帯保証」を返す。保証期間・保証状態・保証名など、
 * その保証を1つの単位として扱うときは常にこれを使う。
 *
 * API(/v1/customer/info) の append_warranties は主保証が先頭とは限らない
 * (取得クエリに ORDER BY が無く実質 id 昇順。主保証を後から手動作成すると特約が先頭に来る)。
 * そのため位置ではなく次の優先順で選ぶ:
 *   1. warranty_type === DEFECT ... 主保証の型。DEFECT が親以外に付くデータは存在しない
 *   2. parent_append_warranty_id が無い ... バックエンドが「メインの追加保証」に使う判定。
 *      DEFECT 以外が主保証になっている保証(warranty_type=9 等)を拾う
 *   3. 先頭 ... 上記で決まらない場合の従来動作
 * ※ API 側でも並び順を主保証先頭に確定させてあるので ③ に落ちても正しくなるが、
 *   アプリと API は独立配信のため、位置に依存しない選択をここで担保する。
 */
export const getRepresentativeAppendWarranty = (
  warranty: Warranty | undefined,
) =>
  findDefectAppendWarranty(warranty) ??
  warranty?.append_warranties?.find(
    (w: WarrantiesAppendWarranty) => !w.parent_append_warranty_id,
  ) ??
  warranty?.append_warranties?.[0];

/** 現在利用できる保証かを、代表付帯保証の状態と開始日から判定する。 */
export const isWarrantyCurrentlyValid = (warranty: Warranty) => {
  const appendWarranty = getRepresentativeAppendWarranty(warranty);
  if (!appendWarranty?.warranty_start_date) return false;
  if (appendWarranty.warranty_status === WARRANTY_STATUS.EXPIRED) return false;
  return (
    differenceInDays(
      new Date(appendWarranty.warranty_start_date),
      new Date(),
    ) <= 0
  );
};

/**
 * 画面遷移先で暗黙に使用する保証を返す。
 * API配列では失効保証が先頭になることがあるため有効保証を優先し、
 * 有効保証がない場合だけ従来どおり先頭を返す。
 */
export const getPreferredCurrentWarranty = (warranties: Warranty[] = []) =>
  warranties.find(isWarrantyCurrentlyValid) ?? warranties[0];

/** 選択中保証と同じ保証番号で、直後の枝番にあたる次期保証を返す。 */
export const findRelevantNextWarranty = (
  warranty?: Warranty,
  nextWarranties?: NextWarranty[],
) => {
  if (!warranty || !nextWarranties?.length) return;
  return nextWarranties.find(
    (nextWarranty) =>
      nextWarranty.warranty_no === warranty.warranty_no &&
      (warranty.branch_no || 0) + NEXT_WARRANTY_BRANCH_INCREMENT ===
        nextWarranty.branch_no,
  );
};

/**
 * 次期保証版の {@link getRepresentativeAppendWarranty}。優先順は現保証側と同じ
 * （① DEFECT → ② `parent_append_warranty_id` が無い → ③ 先頭）。オブジェクト形状も吸収する。
 */
export const getRepresentativeNextAppendWarranty = (
  warranty: NextWarranty | undefined,
) => {
  const appendWarranties = toNextAppendWarrantyList(
    warranty?.append_warranties,
  );
  return (
    findNextDefectAppendWarranty(warranty) ??
    appendWarranties.find((w) => !w.parent_append_warranty_id) ??
    appendWarranties[0]
  );
};

export const switchWarrantyPartsTitleByCoverageType = (
  type: WarrantyCoverageType | undefined | null,
) => {
  switch (type) {
    case warrantyCoverageType.COVERED:
      return '保証対象外部位';
    case warrantyCoverageType.NOT_COVERED:
      return '保証対象部位';
    default:
      return '保証対象部位';
  }
};

export const isCancellationInProcess = (
  applyStatus: ApplyStatus | undefined,
  warrantyStatus: number | undefined,
) => {
  return (
    warrantyStatus === WARRANTY_STATUS.VALID &&
    (applyStatus === APPLY_STATUS.DURING_CANCELLATION_APPLICATION ||
      applyStatus === APPLY_STATUS.OUTSIDE_CANCELLATION_APPLICATION)
  );
};

export const getDescriptionDocument = (documentType?: number) => {
  switch (documentType) {
    case WARRANTY_DOCUMENT_TYPE.CONTRACT_PHOTO:
      return 'お車をご購入いただきました際のご契約書です。';
    case WARRANTY_DOCUMENT_TYPE.CAR_INSPECTION_PHOTO:
      return 'お車の車検証（自動車検査証）です。';
    case WARRANTY_DOCUMENT_TYPE.CONDITION_SHEET_PHOTO:
      return 'ご納車時のお車の状態が記されたものです。';
    case WARRANTY_DOCUMENT_TYPE.EXTERIOR_PHOTO:
      return '車輛の外装状態がわかる写真です。';
    case WARRANTY_DOCUMENT_TYPE.INTERIOR_PHOTO:
      return '車輛の内装状態がわかる写真です。';
    case WARRANTY_DOCUMENT_TYPE.METER_PHOTO:
      return 'お車のメーターパネル箇所の写真です。';
    default:
      return '';
  }
};

export const renderDescriptionDocument = (
  name?: string,
  documentType?: number,
) => {
  if (!documentType || !getDescriptionDocument(documentType) || !name)
    return '';

  return name + 'とは、' + getDescriptionDocument(documentType);
};
